import * as THREE from 'three';
import { cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The cleric's last road (docs/sendas/clerigo.md, chapter 1): its people and its limpets, painted miniatures.
 *
 * - Tessa on the stretcher (`tessaLitter`): two ash poles and a canvas bed between, slung low; Tessa lying on it under a
 *   blanket of grey wool with a slate stripe, propped on a pillow, white hair in a knot, her old hands folded on the
 *   blanket, the Order's stole's grey hem showing; at the head a tall pole with an iron arm, and the lantern hanging
 *   from it (`tessaLitterBare`: the same with the lantern off the pole, the chapel's, where the lantern is drawn apart).
 * - Piet: a shepherd of the terraces, nineteen, thin, a sheepskin vest over a dark wool shirt, a knitted cap, a band of
 *   raw wool on his arm like the hero's; his breath white.
 * - Ludo: a woodcutter of Musgoviejo, broad and short-necked, a heavy coat, a dark beard, his axe slung on his back.
 * - The boy from Musgoviejo: small, a scarf, a lantern of his own, out of breath.
 * - A limpet of ink: flat, a ribbed shell of black ink a hand across, its rim of feelers, a sick violet sheen under it.
 *
 * Colors: cold greys and slate, the wool pale and cold (never cream: a warm white under the lantern turns pink), the
 * lantern's light white; the ink black with violet in it. Never gold.
 */

const ASH = '#5d5248';
const ASH_D = '#40372f';
const CANVAS = '#7c7f83';
const WOOL = '#c6cbd2';
const WOOL_D = '#9da3ab';
const SLATE = '#3f4652';
const IRON = '#2c3036';
const SKIN_OLD = '#c9a594';
const SKIN = '#d2a88e';
const HAIR_WHITE = '#e4e6ea';
const INK = '#0b0912';
const INK_SHEEN = '#3a2a5a';

/** The lantern of the stretcher: an iron cage, its glass and a cold white flame (a glow, no light). */
const lantern = (parent: THREE.Object3D, at: THREE.Vector3): THREE.Group => {
  const l = G(parent, at.x, at.y, at.z);
  const iron = metal(IRON, { roughness: 0.5 });
  M(new THREE.CylinderGeometry(0.075, 0.09, 0.03, 8), iron, l).position.y = 0.12;
  M(new THREE.CylinderGeometry(0.09, 0.075, 0.03, 8), iron, l).position.y = -0.12;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    M(new THREE.BoxGeometry(0.012, 0.24, 0.012), iron, l).position.set(Math.cos(a) * 0.08, 0, Math.sin(a) * 0.08);
  }
  M(new THREE.CylinderGeometry(0.068, 0.068, 0.2, 10), std('#e9eef6', { roughness: 0.15, emissive: '#cfdcf0', emissiveIntensity: 0.9, transparent: true, opacity: 0.85 }), l);
  M(new THREE.SphereGeometry(0.035, 8, 6), emis('#ffffff', '#f4f8ff', 2.4), l);
  const ring0 = M(new THREE.TorusGeometry(0.03, 0.008, 4, 8), iron, l);
  ring0.position.y = 0.16;
  glowSprite(l, '#eef4ff', 1.1, V(0, 0, 0), 0.85);
  return l;
};

/** Tessa on the stretcher; `bare`: without its lantern (the chapel draws it where it hangs). */
const tessaLitter = (o: BaseOpts | undefined, bare: boolean): Model => {
  const c = begin(o, 0.9, 881);
  const g = c.g;
  const ash = std(ASH, { roughness: 0.8 });
  const ashD = std(ASH_D, { roughness: 0.85 });
  // The two poles, front to back (along +Z), and the canvas bed slung between them, low.
  for (const k of [-1, 1]) {
    const pole = M(new THREE.CylinderGeometry(0.03, 0.03, 2.5, 6), ash, g);
    pole.rotation.x = Math.PI / 2;
    pole.position.set(k * 0.27, 0.78, 0);
    // The handles, worn darker at both ends.
    for (const z of [-1.14, 1.14]) {
      const hd = M(new THREE.CylinderGeometry(0.034, 0.034, 0.22, 6), ashD, g);
      hd.position.set(k * 0.27, 0.78, z);
      hd.rotation.x = Math.PI / 2;
    }
  }
  // Two crossbars and the canvas, sagging a little.
  for (const z of [-0.78, 0.78]) {
    const bar = M(new THREE.CylinderGeometry(0.022, 0.022, 0.56, 5), ashD, g);
    bar.rotation.z = Math.PI / 2;
    bar.position.set(0, 0.78, z);
  }
  const bed = M(new THREE.BoxGeometry(0.52, 0.05, 1.62), std(CANVAS, { roughness: 1 }), g);
  bed.position.set(0, 0.73, 0);
  // Tessa: the blanket over her, a long low mound; her feet toward the back pole (−Z), her head at the front.
  const wool = cloth(WOOL_D);
  const blanket = M(new THREE.CapsuleGeometry(0.2, 0.92, 4, 10), wool, g);
  blanket.rotation.x = Math.PI / 2;
  blanket.scale.set(1.15, 1, 0.62);
  blanket.position.set(0, 0.84, -0.14);
  // The blanket's slate stripes across.
  for (const z of [-0.5, 0.12]) {
    const st = M(new THREE.BoxGeometry(0.47, 0.03, 0.08), cloth(SLATE), g);
    st.position.set(0, 0.97, z);
  }
  // The grey hem of the Order's stole over the blanket's edge.
  const stole = M(new THREE.BoxGeometry(0.08, 0.02, 0.52), cloth('#d7dbe2'), g);
  stole.position.set(0.12, 0.99, 0.18);
  // Her shoulders, her hands folded on the blanket.
  const shoulders = M(new THREE.CapsuleGeometry(0.13, 0.16, 4, 8), cloth('#4b505a'), g);
  shoulders.rotation.z = Math.PI / 2;
  shoulders.position.set(0, 0.88, 0.42);
  const skin = std(SKIN_OLD, { roughness: 0.9 });
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.04, 7, 5), skin, g).position.set(k * 0.05, 0.99, 0.24);
  // The pillow, her head on it, turned a little up toward the front; white hair in a knot.
  const pillow = M(new THREE.BoxGeometry(0.34, 0.08, 0.2), cloth('#b6bcc4'), g);
  pillow.position.set(0, 0.82, 0.66);
  const head = G(g, 0, 0.93, 0.62);
  head.rotation.x = -0.55;
  M(new THREE.SphereGeometry(0.1, 12, 10), skin, head);
  const hair = std(HAIR_WHITE, { roughness: 1, emissive: '#2a2e36' });
  const cap = M(new THREE.SphereGeometry(0.106, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, head);
  cap.position.set(0, 0.01, -0.012);
  M(new THREE.SphereGeometry(0.05, 8, 6), hair, head).position.set(0, 0.05, -0.1);
  const nose = M(new THREE.ConeGeometry(0.016, 0.04, 5), skin, head);
  nose.position.set(0, 0.0, 0.1);
  nose.rotation.x = Math.PI / 2;
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.008, 0.01), std('#4b3a34'), head).position.set(k * 0.036, 0.02, 0.092);
  // The lantern's pole at the head, its iron arm reaching forward over her.
  const pole = M(new THREE.CylinderGeometry(0.022, 0.026, 1.0, 6), ashD, g);
  pole.position.set(0.27, 1.26, 0.9);
  const arm = M(new THREE.BoxGeometry(0.02, 0.02, 0.34), metal(IRON, { roughness: 0.5 }), g);
  arm.position.set(0.27, 1.74, 1.04);
  let flame: THREE.Group | null = null;
  if (!bare) flame = lantern(g, V(0.27, 1.5, 1.18));
  const tick = (n: number): void => {
    if (flame) flame.rotation.z = Math.sin(n * 0.9) * 0.08;
  };
  return done(c, 1.4, tick);
};

/** Tessa standing (her face in the scenes' portraits): small and bent, the Order's grey stole, white hair in a knot. */
const tessa = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 886);
  const robe = cloth('#4b505a');
  const skin = std(SKIN_OLD, { roughness: 0.9 });
  const f = figure({ legs: robe, boots: leather('#2a221c'), torso: robe, shoulders: robe, arms: robe, forearms: robe, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.86, 0.9, 0.86);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The stole: two bands of cold white down the front, a grey hem.
  for (const k of [-1, 1]) {
    const band = M(new THREE.BoxGeometry(0.06, 0.5, 0.02), cloth('#d7dbe2'), b);
    band.position.set(k * 0.07, 1.2, 0.2);
  }
  const hair = std(HAIR_WHITE, { roughness: 1, emissive: '#2a2e36' });
  const cap = M(new THREE.SphereGeometry(0.128, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
  cap.position.set(0, 1.655, -0.012);
  M(new THREE.SphereGeometry(0.06, 8, 6), hair, h).position.set(0, 1.69, -0.12);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.652, 0.114);
    M(new THREE.BoxGeometry(0.03, 0.006, 0.01), std('#8a7266'), h).position.set(k * 0.042, 1.632, 0.116);
  }
  const nose = M(new THREE.ConeGeometry(0.017, 0.045, 5), skin, h);
  nose.position.set(0, 1.628, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  f.rig.head.rotation.x = 0.12;
  return done(c, 1.55, () => undefined, f.rig);
};

/** Piet: the shepherd, thin, the sheepskin vest, the knitted cap. */
const piet = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 882);
  const shirt = cloth('#3a3d44');
  const skin = std(SKIN, { roughness: 0.85 });
  const fleece = cloth(WOOL);
  const f = figure({ legs: cloth('#33363c'), boots: leather('#2a221c'), torso: fleece, shoulders: fleece, arms: shirt, forearms: shirt, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.9, 1.0, 0.9);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The vest's fleece edge, rough.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    M(new THREE.SphereGeometry(0.045, 6, 5), fleece, b).position.set(Math.cos(a) * 0.2, 1.0, Math.sin(a) * 0.17);
  }
  // A shepherd's band of raw wool on his arm.
  ring(b, 0.062, 0.024, cloth(WOOL), 1.27, 4, 10).position.set(0.255, 1.27, 0);
  // The knitted cap, slate, its fold.
  const capM = cloth(SLATE);
  const cap = M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), capM, h);
  cap.position.set(0, 1.66, -0.01);
  ring(h, 0.128, 0.025, capM, 1.63, 4, 12);
  // Hair under it, brown, and a young face.
  const hair = std('#4a3326', { roughness: 1 });
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.05, 0.06, 0.06), hair, h).position.set(k * 0.11, 1.6, -0.02);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.656, 0.114);
  const nose = M(new THREE.ConeGeometry(0.017, 0.045, 5), std('#c99c84', { roughness: 0.85 }), h);
  nose.position.set(0, 1.628, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  // Arms back, the hands down by the hips: holding the front poles behind him.
  f.rig.armR.rotation.x = 0.35;
  f.rig.armL.rotation.x = 0.35;
  return done(c, 1.7, () => undefined, f.rig);
};

/** Ludo: the woodcutter, broad, the heavy coat, the beard, the axe on his back. */
const ludo = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 883);
  const coat = cloth('#3d3b38');
  const skin = std('#c49a84', { roughness: 0.88 });
  const f = figure({ legs: cloth('#2a2a2e'), boots: leather('#211b16'), torso: coat, shoulders: coat, arms: coat, forearms: cloth('#4a4743'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(1.14, 1.0, 1.12);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The coat's skirt down to the knees, a belt of rope.
  const skirt = M(new THREE.CylinderGeometry(0.2, 0.26, 0.34, 10), coat, b);
  skirt.position.y = 0.84;
  ring(b, 0.21, 0.02, std('#6a6258', { roughness: 1 }), 0.98, 4, 12);
  // The axe across his back: its haft and its head.
  const axe = G(b, 0, 1.2, -0.2);
  axe.rotation.z = 0.7;
  limb(axe, V(0, -0.42, 0), V(0, 0.42, 0), 0.02, 0.022, std(ASH, { roughness: 0.8 }), 5);
  const blade = M(new THREE.BoxGeometry(0.16, 0.12, 0.02), metal('#5a6069', { roughness: 0.4 }), axe);
  blade.position.set(0.07, 0.38, 0);
  // The beard, dark and full; bushy brows; hair cropped.
  const dark = std('#2b2420', { roughness: 1 });
  const beard = M(new THREE.SphereGeometry(0.115, 10, 8, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), dark, h);
  beard.position.set(0, 1.58, 0.05);
  beard.scale.set(1.15, 1.1, 0.95);
  const top = M(new THREE.SphereGeometry(0.125, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.42), dark, h);
  top.position.set(0, 1.655, -0.01);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.05, 0.018, 0.025), dark, h).position.set(k * 0.044, 1.684, 0.11);
  }
  const nose = M(new THREE.SphereGeometry(0.03, 8, 6), std('#b9876e', { roughness: 0.8 }), h);
  nose.position.set(0, 1.62, 0.13);
  // Arms forward, the hands at the back poles in front of him.
  f.rig.armR.rotation.x = -0.45;
  f.rig.armL.rotation.x = -0.45;
  return done(c, 1.72, () => undefined, f.rig);
};

/** The boy from Musgoviejo: small, a scarf, his own little lantern. */
const musgoBoy = (o?: BaseOpts): Model => {
  const c = begin(o, 0.45, 884);
  const coat = cloth('#4a5260');
  const skin = std(SKIN, { roughness: 0.85 });
  const f = figure({ legs: cloth('#2d3038'), boots: leather('#221c18'), torso: coat, shoulders: coat, arms: coat, forearms: coat, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.setScalar(0.7);
  const b = f.bodyAbs;
  const h = f.headAbs;
  ring(b, 0.13, 0.04, cloth('#9b3a30'), 1.45, 5, 12);
  const hair = std('#3a2a20', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.133, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.6), hair, h);
  top.position.set(0, 1.652, -0.012);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.013, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.656, 0.114);
  lantern(G(f.rhAbs, 0.29, 0.78, 0.16), V(0, 0, 0)).scale.setScalar(0.6);
  return done(c, 1.2, () => undefined, f.rig);
};

/** A limpet of ink: flat, ribbed, its rim of feelers; a violet sheen under it that breathes. */
export const lapa = (o?: BaseOpts): Model => {
  const c = begin(o, 0.3, 885);
  const g = c.g;
  // (Big for what it is: at the game's distance it has to read on the stretcher's canvas and on the snow.)
  g.scale.setScalar(2.1);
  const ink = std(INK, { roughness: 0.25, metalness: 0.2, emissive: '#120a20' });
  const sheen = emis('#1a1028', INK_SHEEN, 1.2);
  const shell = M(new THREE.ConeGeometry(0.3, 0.16, 14, 1), ink, g);
  shell.position.y = 0.09;
  // Ribs down the shell.
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    const rib = M(new THREE.BoxGeometry(0.03, 0.02, 0.28), std('#1c1626', { roughness: 0.5 }), g);
    rib.position.set(Math.cos(a) * 0.15, 0.09, Math.sin(a) * 0.15);
    rib.rotation.set(0.5, -a + Math.PI / 2, 0);
  }
  // The sheen under its rim, and the feelers round it.
  const glow = M(new THREE.CylinderGeometry(0.32, 0.34, 0.03, 16), sheen, g);
  glow.position.y = 0.015;
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const f = M(new THREE.ConeGeometry(0.018, 0.14, 4), ink, g);
    f.position.set(Math.cos(a) * 0.33, 0.02, Math.sin(a) * 0.33);
    f.rotation.set(Math.PI / 2, 0, -a + Math.PI / 2);
  }
  // A pale eye of a slit at its crown.
  M(new THREE.BoxGeometry(0.1, 0.012, 0.02), emis('#d8d0ff', '#a090ff', 1.6), g).position.set(0, 0.17, 0.02);
  const tick = (n: number): void => {
    shell.scale.set(1 + Math.sin(n * 2.1) * 0.06, 1 - Math.sin(n * 2.1) * 0.08, 1 + Math.sin(n * 2.1) * 0.06);
    (glow.material as THREE.MeshStandardMaterial).emissiveIntensity = 1 + 0.5 * Math.sin(n * 1.3);
  };
  return done(c, 0.3, tick);
};

/** The cleric's last road's people by name (null: not one of them). */
export const caminoModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'tessaLitter':
      return tessaLitter(o, false);
    case 'tessaLitterBare':
      return tessaLitter(o, true);
    case 'tessa':
      return tessa(o);
    case 'piet':
      return piet(o);
    case 'ludo':
      return ludo(o);
    case 'musgoBoy':
      return musgoBoy(o);
    default:
      return null;
  }
};
